import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, RefreshCw, MinusCircle, GraduationCap, Languages, SpellCheck, X, Check, Copy,
} from "lucide-react";

const MODES = [
  { key: "rewrite", label: "Rewrite", icon: Sparkles, description: "CQC-structured professional note" },
  { key: "shorter", label: "Shorter", icon: MinusCircle, description: "2-3 sentence handover version" },
  { key: "professional", label: "More Professional", icon: GraduationCap, description: "Formal UK care sector wording" },
  { key: "easy_read", label: "Translate to Easy Read", icon: Languages, description: "Simple sentences for LD services" },
  { key: "grammar", label: "Correct Grammar", icon: SpellCheck, description: "Fix grammar & spelling only" },
];

/**
 * AI Care Note Assistant — floating trigger + rewrite dialog.
 *
 * Props:
 *  - initialNote: string        seed text (pre-fill from RecordDialog)
 *  - clientId: string           optional context enrichment
 *  - onApply(rewritten): void   manager approval → applies text back into caller
 */
export default function CareNoteAssistant({ initialNote = "", clientId, onApply }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(initialNote);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("rewrite");
  const [preview, setPreview] = useState("");

  const openWith = () => {
    setNote(initialNote || "");
    setPreview("");
    setOpen(true);
  };

  const runMode = async (m) => {
    if (!note.trim()) { toast.error("Type a note first"); return; }
    setBusy(true);
    setMode(m);
    try {
      const r = await api.post("/ai/care-notes/rewrite", { note, mode: m, client_id: clientId });
      setPreview(r.data.rewritten || "");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const approve = () => {
    if (!preview.trim()) return;
    onApply?.(preview);
    toast.success("Note applied — review and save");
    setOpen(false);
  };

  const copyPreview = async () => {
    try {
      await navigator.clipboard.writeText(preview.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim());
      toast.success("Copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={openWith}
        data-testid="btn-ai-care-note"
        className="h-8 text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
      >
        <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Care Assistant
      </Button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="care-note-assistant">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 w-full max-w-3xl max-h-[90vh] rounded-md flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-slate-200 dark:border-slate-700 px-5 py-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-violet-700 dark:text-violet-400 font-semibold flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Ayobell Premium · AI Care Assistant
                </div>
                <h2 className="font-heading text-lg font-semibold">Rewrite carer note to CQC-compliant language</h2>
                <div className="text-[11px] text-slate-500 mt-0.5">Structured under CQC headings · person-centred · never invents facts. Manager approval required before saving.</div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="care-note-close" className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Your raw note</label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={4}
                  data-testid="care-note-input"
                  placeholder='e.g. "John had breakfast, took medication, went shopping and was happy today."'
                  className="text-sm bg-white dark:bg-slate-900"
                />
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Choose a style</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {MODES.map((m) => {
                    const Icon = m.icon;
                    const on = mode === m.key && preview;
                    return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => runMode(m.key)}
                        disabled={busy}
                        data-testid={`care-note-mode-${m.key}`}
                        title={m.description}
                        className={`flex flex-col items-center gap-1 px-2 py-2 rounded-md border text-xs font-medium transition-colors disabled:opacity-60 ${on ? "bg-violet-700 text-white border-violet-700" : "bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:border-violet-500"}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{m.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {busy && (
                <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 p-3 text-xs text-slate-600 dark:text-slate-300 flex items-center gap-2">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Rewriting with Claude Sonnet 4.5…
                </div>
              )}

              {preview && !busy && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">AI suggestion · {MODES.find((m) => m.key === mode)?.label}</div>
                    <button
                      onClick={copyPreview}
                      data-testid="care-note-copy"
                      className="text-[11px] text-slate-500 hover:text-sky-700 inline-flex items-center gap-1"
                    ><Copy className="w-3 h-3" /> Copy</button>
                  </div>
                  <div
                    className="rounded-md border border-violet-300 bg-violet-50/40 dark:bg-violet-950/20 dark:border-violet-800 p-3 text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap"
                    data-testid="care-note-preview"
                    dangerouslySetInnerHTML={{ __html: preview.replace(/\n/g, "<br />") }}
                  />
                  <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800">
                    ⚠️ Manager review required. Read carefully — only apply if factually accurate. The AI may miss nuance a human carer would spot.
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-slate-200 dark:border-slate-700 px-5 py-3 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="care-note-cancel">Cancel</Button>
              <Button
                onClick={approve}
                disabled={!preview || busy}
                data-testid="care-note-approve"
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                <Check className="w-4 h-4 mr-1.5" /> Approve & apply to note
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
